import type { ComponentPropsWithRef, ComponentPropsWithoutRef, ReactNode } from 'react';
import type { FieldValues, Path } from 'react-hook-form';

import { cn } from '@pesto/utils';
import { forwardRef } from 'react';

import { TextArea } from '../../components/TextArea';

type FormJsonInputBaseProps<TFieldValues extends FieldValues> = {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: string;
  description?: ReactNode;
  required?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  id?: string;
  name: Path<TFieldValues>;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
};

export type FormJsonInputProps<TFieldValues extends FieldValues = FieldValues> = FormJsonInputBaseProps<TFieldValues> &
  Omit<ComponentPropsWithoutRef<typeof TextArea>, keyof FormJsonInputBaseProps<TFieldValues> | 'onChange' | 'value'>;

export const FormJsonInput = forwardRef(
  <TFieldValues extends FieldValues = FieldValues>(
    {
      label,
      helperText,
      error,
      description,
      required,
      wrapperClassName,
      labelClassName,
      id: providedId,
      name,
      readOnly,
      disabled,
      placeholder,
      value,
      onChange,
      className,
      'aria-describedby': ariaDescribedBy,
      ...props
    }: FormJsonInputProps<TFieldValues>,
    ref: ComponentPropsWithRef<typeof TextArea>['ref'],
  ) => {
    const id = providedId || `form-field-${name}`;
    const helperId = `${id}-helper`;
    const errorId = `${id}-error`;
    const descriptionId = `${id}-description`;

    const describedBy = [ariaDescribedBy, helperText && helperId, error && errorId, description && descriptionId]
      .filter(Boolean)
      .join(' ');

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(event.target.value);
    };

    return (
      <div className={cn('space-y-2', wrapperClassName)}>
        {label && (
          <div className="flex justify-between">
            <label
              htmlFor={id}
              className={cn(
                'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                error && 'text-destructive',
                labelClassName,
              )}
            >
              {label}
              {required && !readOnly && <span className="text-destructive ml-1">*</span>}
            </label>
            {description && (
              <span id={descriptionId} className="text-muted-foreground text-xs">
                {description}
              </span>
            )}
          </div>
        )}
        <TextArea
          ref={ref}
          id={id}
          name={name}
          value={value ?? ''}
          onChange={handleChange}
          aria-describedby={describedBy || undefined}
          aria-invalid={!!error}
          aria-required={required}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={readOnly || disabled}
          className={cn(error && 'border-destructive', className)}
          {...props}
        />
        {(helperText || error) && (
          <div className="space-y-1">
            {helperText && (
              <p id={helperId} className="text-muted-foreground text-sm">
                {helperText}
              </p>
            )}
            {error && (
              <p id={errorId} className="text-destructive text-sm">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

FormJsonInput.displayName = 'FormJsonInput';
